import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { getRef } from "../../lib/ref";

const EVENT_TYPES = ["Wedding", "Milestone birthday", "Corporate retreat", "Family reunion", "Other gathering"];

export const EventInquiry = ({ property, venue }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_date: "", event_guests: "", event_type: "Wedding", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/inquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        purpose: `Event · ${form.event_type}`,
        event_date: form.event_date,
        event_guests: form.event_guests,
        message: form.message,
        venue: venue || "Pebble Farms",
        ref: getRef(),
      });
      setDone(true);
      toast.success("Event inquiry sent — we'll be in touch shortly.");
    } catch {
      toast.error("Couldn't send — please call or text (210) 274-3556.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-white border border-charcoal/15 rounded-xl px-4 py-3.5 text-base outline-none focus:border-copper transition-colors";

  return (
    <section className="pb-16 sm:pb-24 -mt-2" aria-label="Event inquiry" data-testid="event-inquiry-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}
          className="bg-creamdark border border-charcoal/10 rounded-3xl p-6 sm:p-10 shadow-sm"
        >
          {done ? (
            <div className="text-center py-6" data-testid="event-inquiry-success">
              <span className="inline-flex p-4 rounded-full bg-sage/15 mb-4"><Check size={26} className="text-sagedeep" aria-hidden /></span>
              <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-charcoal">Inquiry sent</h3>
              <p className="text-soft mt-3 max-w-md mx-auto leading-relaxed">
                Thanks, {form.name.split(" ")[0] || "friend"} — the Elite STR team will reach out about your {form.event_type.toLowerCase()} shortly.
                Need us sooner? Call or text <a href={`tel:${property.phone_tel}`} className="font-semibold text-copperdeep underline underline-offset-2">{property.phone_display}</a>.
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-7">
                <span className="inline-flex p-3 rounded-full bg-copper/10 mb-3"><Heart size={20} className="text-copper" aria-hidden /></span>
                <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-charcoal">Tell us about your event</h3>
                <p className="text-soft mt-2">A few details and we'll confirm availability, event terms, and next steps.</p>
              </div>
              <form onSubmit={submit} className="space-y-4" data-testid="event-inquiry-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required value={form.name} onChange={set("name")} placeholder="Name" aria-label="Name" className={inputCls} data-testid="event-name-input" />
                  <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" aria-label="Email" className={inputCls} data-testid="event-email-input" />
                  <input type="tel" value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" aria-label="Phone" className={inputCls} data-testid="event-phone-input" />
                  <select value={form.event_type} onChange={set("event_type")} aria-label="Event type" className={inputCls} data-testid="event-type-select">
                    {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <label className="block">
                    <span className="text-xs font-semibold text-soft">Preferred event date</span>
                    <input type="date" value={form.event_date} onChange={set("event_date")} aria-label="Preferred event date" className={inputCls + " mt-1"} data-testid="event-date-input" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-soft">Estimated guests</span>
                    <input inputMode="numeric" value={form.event_guests} onChange={set("event_guests")} placeholder="e.g. 60" aria-label="Estimated guests" className={inputCls + " mt-1"} data-testid="event-guests-input" />
                  </label>
                </div>
                <textarea value={form.message} onChange={set("message")} rows={4}
                  placeholder="Tell us about your celebration — ceremony plans, vendors, anything you're dreaming up…"
                  aria-label="About your event" className={inputCls} data-testid="event-message-input" />
                <button type="submit" disabled={busy}
                  className="w-full bg-copper text-cream font-semibold rounded-full py-4 text-lg hover:bg-copperdeep transition-colors disabled:opacity-50"
                  data-testid="event-submit-button">
                  {busy ? "Sending…" : "Send event inquiry"}
                </button>
                <p className="text-center text-xs text-soft">All events require advance host approval — an overnight booking does not itself approve an event.</p>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
};
